/** Login Tracking Utilities for CleverTap & Google Analytics */
import trackClevertapEvents from "./trackClevertapEvents";

export type LoginMethod = "Password" | "OTP" | "Social" | "OneTap";
export type CredentialType = "email" | "mobile" | "social";
export type SocialProvider = "Google" | "Linkedin" | "Facebook";

interface LoginTrackingProps {
  loginMethod: LoginMethod;
  credentialType: CredentialType;
  username?: string;
  socialProvider?: SocialProvider;
  isOneTap?: boolean;
}

export const trackLogin = ({
  loginMethod,
  credentialType,
  username,
  socialProvider,
  isOneTap = false,
}: LoginTrackingProps) => {
  let loginType = "";
  if (loginMethod === "OneTap") {
    loginType = `login via OneTap (${socialProvider || "Google"})`;
  } else if (loginMethod === "Social") {
    loginType = `login via Social (${socialProvider})`;
  } else if (loginMethod === "OTP") {
    loginType = `login via OTP (${credentialType})`;
  } else if (loginMethod === "Password") {
    loginType = `login via password (${credentialType})`;
  }

  // Build CleverTap properties
  const ctProps: Record<string, string> = {
    login_type: loginType,
    login_method: loginMethod,
    credential_type: credentialType,
    referrer: typeof document !== "undefined" ? document.referrer : "",
  };

  // Add social provider if applicable
  if (socialProvider) {
    ctProps.social_provider = socialProvider;
  }

  // Add one-tap flag
  if (isOneTap) {
    ctProps.is_one_tap = "true";
  }

  // Add username/email/mobile
  if (username) {
    if (credentialType === "email") {
      ctProps.email_id = username;
    } else if (credentialType === "mobile") {
      ctProps.mobile = username;
    }
  }

  // Track with CleverTap
  // trackClevertapEvents.eventPush("Login_Filled", ctProps);

  // Track onUserLogin for user identification
  if (username && credentialType !== "social") {
    // trackClevertapEvents.onuserLoginPush({ Email: username });
  }

  // Track with Google Analytics
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "login",
      eventCategory: "Log-In",
      eventAction:
        loginMethod === "OneTap"
          ? "OneTap-Login"
          : loginMethod === "Social"
            ? socialProvider
            : loginMethod === "OTP"
              ? "Login-via-OTP"
              : "Login-CTA",
      eventLabel: credentialType,
      loginMethod: loginMethod,
    });
  }
};

/**
 * Tracks OTP send event
 */
export const trackOTPSent = (
  credentialType: "email" | "mobile",
  username: string,
) => {
  const ctProps: Record<string, string> = {
    login_type: `login via OTP (${credentialType})`,
    login_method: "OTP",
    credential_type: credentialType,
    action: "OTP_SENT",
  };

  if (credentialType === "email") {
    ctProps.email_id = username;
  } else {
    ctProps.mobile = username;
  }

  // trackClevertapEvents.eventPush("Login_Filled", ctProps);

  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "otp_send",
      eventCategory: "Log-In",
      eventAction: "Login-via-OTP",
      eventLabel: credentialType,
    });
  }
};

/**
 * Tracks one-tap login shown event (for Google One Tap)
 * Call this when the one-tap prompt is displayed
 */
export const trackOneTapShown = () => {
  // trackClevertapEvents.eventPush("Google One Tap Shown", {
  //   referrer: typeof document !== "undefined" ? document.referrer : "",
  // });
};

/**
 * Tracks one-tap login closed event (for Google One Tap)
 * Call this when the user dismisses the one-tap prompt
 */
export const trackOneTapClosed = () => {
  // trackClevertapEvents.eventPush("Google One Tap Closed", {
  //   referrer: typeof document !== "undefined" ? document.referrer : "",
  // });
};

/**
 * All possible login types tracked in the application
 */
export const LOGIN_TYPES = {
  PASSWORD_EMAIL: "login via password (email)",
  PASSWORD_MOBILE: "login via password (mobile)",
  OTP_EMAIL: "login via OTP (email)",
  OTP_MOBILE: "login via OTP (mobile)",
  SOCIAL_GOOGLE: "login via Social (Google)",
  SOCIAL_LINKEDIN: "login via Social (Linkedin)",
  SOCIAL_FACEBOOK: "login via Social (Facebook)",
  ONETAP_GOOGLE: "login via OneTap (Google)",
} as const;
